import React, {useCallback} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import Animated, {
  FadeInDown,
  FadeOut,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';
import {Theme} from '../../theme/lightTheme';
import TextGeneric from '../../components/textGeneric';
import Icon from '../../components/icon';
import MyPressable from '../myPressable';
import LinearGradient from 'react-native-linear-gradient';
import {PixelRatio, Platform} from 'react-native';
import {CountryListItemProps} from '../../interfaces/components/countryListItem';
import {CountryListItemHeight} from '../../connected-components/countryPickerModal';

export const languageListItemHeight = Platform.OS === 'web' ? 40 : 118 / PixelRatio.get();
const ContentContainer = styled(MyPressable)`
  width: 100%;
  height: 100%;
  padding-horizontal: ${({theme}) => theme.text.s55}px;
  justify-content: center;
`;
const CountryItemContentContainer = styled(Animated.View)`
  width: 100%;
`;
const TextContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;
const Row = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;
const Text = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s8}px;
  color: ${({theme}) => theme.text.title};
  height: 100%;
`;

/**
 * Displays a Country List Item.
 * @param {boolean} isSelected Indicates whether the item is selected.
 * @param {CountryListItemProps} props The properties of the CountryListItem component.
 * @returns {React.ReactElement} The CountryListItem component.
 */
const CountryListItem: React.FC<CountryListItemProps> = ({
  onPress,
  currentValue,
  item,
  style,
}: CountryListItemProps): React.ReactElement => {
  const theme = useTheme() as Theme;

  const onPressHandler = useCallback(() => {
    onPress && onPress(item);
  }, [onPress]);

  return (
    <LinearGradient
      style={[
        {
          width: '90%',
          height: languageListItemHeight,
          alignSelf: 'center',
          borderRadius: 8,
          overflow: 'hidden',
        },
        style,
      ]}
      start={{x: 0.0, y: 0.25}}
      end={{x: 0.5, y: 1.0}}
      colors={[theme.text.light + '77', theme.border + '55']}>
      <CountryItemContentContainer entering={FadeInDown} exiting={FadeOut}>
        <ContentContainer
          borderRadius={8}
          containerStyle={{
            alignSelf: 'center',
            height: CountryListItemHeight,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderWidth: 1,
            borderColor: theme.border + '77',
            width: '100%',
          }}
          onPress={onPressHandler}>
          <Row>
            <TextContainer>
              <Text semiBold>{item.name}</Text>
            </TextContainer>
            {currentValue === item.name && (
              <Animated.View entering={ZoomIn} exiting={ZoomOut}>
                <Icon
                  name={'check'}
                  color={theme.primary}
                  size={theme.text.s6}
                />
              </Animated.View>
            )}
          </Row>
        </ContentContainer>
      </CountryItemContentContainer>
    </LinearGradient>
  );
};

// Wrap with React.memo to add memoization
export default React.memo(CountryListItem, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.currentValue === nextProps.currentValue &&
    prevProps.onPress === nextProps.onPress &&
    prevProps.style === nextProps.style
  );
});
